import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Row, Col, Button, Spinner, Alert } from 'reactstrap';
import { httpClient } from '../../../api/httpClient';
import { urlObtenerPdfCotizacionMoto, urlEnviarPdfCotizacionMoto } from '../api/urls';
import { emp, plataforma1 } from '../../../constants/urls';

/**
 * Componente oficial de Valor del Seguro para Motos.
 * Mantiene la estética estándar del portal AIG e incluye los botones dinámicos de ACTUALIZAR / INICIAR EMISIÓN
 * así como la descarga del PDF de cotización desde S3 y su envío por correo electrónico.
 */
const ValoresCotizacionMoto = ({
    valorTotal,
    cotizacion,
    huboCambio,
    onActualizar,
    onIniciarEmision,
    actualizando,
    idCotizacion,
    idCarrito,
    pdfUrlProp,
    textoBotonEmision,
    requiereTerminos,
    terminosAceptados,
    onChangeTerminos,
    mostrarDescargaYEnvio,
    deshabilitarAcciones,
    esBloqueanteEmision,
    txtBloqueoEmision
}) => {
    const [mostrarImpuestos, setMostrarImpuestos] = useState(false);
    const [correoEnvio, setCorreoEnvio] = useState('');
    const [terminosInternos, setTerminosInternos] = useState(false);
    
    var mensajeBloqueo = (
        txtBloqueoEmision ||
        (cotizacion && cotizacion.txtBloqueoEmision) ||
        (valorTotal && valorTotal.txtBloqueoEmision) ||
        ''
    );
    var esBloqueo = Boolean(
        (esBloqueanteEmision || (cotizacion && cotizacion.esBloqueanteEmision) || (valorTotal && valorTotal.esBloqueanteEmision)) &&
        mensajeBloqueo
    );
    
    // Estado para el PDF
    const [cargandoPdf, setCargandoPdf] = useState(false);
    const [pdfUrlInternal, setPdfUrlInternal] = useState('');
    const [enviandoCorreo, setEnviandoCorreo] = useState(false);
    const [mensajeFeedbackEnvio, setMensajeFeedbackEnvio] = useState(null);

    var aceptaTerminos = terminosAceptados !== undefined ? terminosAceptados : terminosInternos;
    var setAceptaTerminos = onChangeTerminos || setTerminosInternos;

    // Obtener idCarrito de prop o URL
    var searchStr = (window.location && window.location.search) || '';
    var queryParams = new URLSearchParams(searchStr);
    var targetIdCarrito = idCarrito || queryParams.get('idCarrito') || queryParams.get('id') || '';

    var activePdfUrl = pdfUrlProp || pdfUrlInternal;
    var mostrarDescargaPdf = Boolean(activePdfUrl);

    // Consultar el PDF de cotización desde el WS sólo si no viene pdfUrlProp
    const consultarPdfWS = useCallback(async () => {
        if (pdfUrlProp || !targetIdCarrito || huboCambio || actualizando) {
            return;
        }
        setCargandoPdf(true);
        try {
            var url = urlObtenerPdfCotizacionMoto + targetIdCarrito;
            var res = await httpClient.get(url);
            if (res && res.transaccion && res.url) {
                setPdfUrlInternal(res.url);
            } else {
                setPdfUrlInternal('');
            }
        } catch (err) {
            console.error('Error al consultar PDF de cotización:', err);
            setPdfUrlInternal('');
        } finally {
            setCargandoPdf(false);
        }
    }, [pdfUrlProp, targetIdCarrito, huboCambio, actualizando]);

    // Reconsultar PDF si no viene pdfUrlProp y estamos listos
    useEffect(() => {
        if (!pdfUrlProp && targetIdCarrito && !huboCambio && !actualizando) {
            consultarPdfWS();
        }
    }, [pdfUrlProp, targetIdCarrito, huboCambio, actualizando, consultarPdfWS]);

    if (!valorTotal && !cotizacion) {
        return null;
    }

    var formatearMoneda = function(monto) {
        var num = parseFloat(monto);
        if (isNaN(num)) {
            return '$0.00';
        }
        return '$' + num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Extraer valores principales
    var primaNeta = (valorTotal && valorTotal.primaNeta) || (cotizacion && cotizacion.primaNeta) || 0;
    var totalImpuestos = (valorTotal && valorTotal.totalImpuestos) || 0;
    var primaTotal = (valorTotal && (valorTotal.primaTotal || valorTotal.totalCarrito)) || (cotizacion && cotizacion.primaTotal) || 0;

    // Desglose de impuestos individuales
    var iva = 0;
    var superBancos = (cotizacion && cotizacion.superBancos) || 0;
    var seguroCampesino = (cotizacion && cotizacion.seguroCampesino) || 0;
    var derechosEmision = (cotizacion && cotizacion.derechoEmision) || 0;

    if (valorTotal && Array.isArray(valorTotal.impuestos)) {
        valorTotal.impuestos.forEach(function(imp) {
            var nombre = (imp.nombre || '').toLowerCase();
            var val = parseFloat(imp.valor) || 0;
            if (nombre.indexOf('iva') !== -1) {
                iva = val;
            } else if (nombre.indexOf('superintendencia') !== -1 || nombre.indexOf('bancos') !== -1) {
                superBancos = val;
            } else if (nombre.indexOf('campesino') !== -1) {
                seguroCampesino = val;
            } else if (nombre.indexOf('emisión') !== -1 || nombre.indexOf('emision') !== -1 || nombre.indexOf('derecho') !== -1) {
                derechosEmision = val;
            }
        });
    } else if (valorTotal) {
        iva = parseFloat(valorTotal.iva_Full_Cobertura || (cotizacion && cotizacion.iva) || 0);
        superBancos = parseFloat(valorTotal.super_bancos_Full_Cobertura || superBancos);
        seguroCampesino = parseFloat(valorTotal.seguro_campesino || seguroCampesino);
        derechosEmision = parseFloat(valorTotal.derecho_emision_Full_Cobertura || derechosEmision);
    }

    if (totalImpuestos === 0 && (iva > 0 || superBancos > 0 || seguroCampesino > 0 || derechosEmision > 0)) {
        totalImpuestos = iva + superBancos + seguroCampesino + derechosEmision;
    }

    var mostrarSeccionDescarga = mostrarDescargaYEnvio !== undefined ? mostrarDescargaYEnvio : !requiereTerminos;

    const handleDescargarPdf = () => {
        if (activePdfUrl) {
            window.open(activePdfUrl, '_blank');
        } else {
            consultarPdfWS();
        }
    };

    const handleEnviarCorreo = async () => {
        if (!correoEnvio || !correoEnvio.trim()) {
            setMensajeFeedbackEnvio({ tipo: 'danger', texto: 'Por favor ingrese un correo electrónico.' });
            return;
        }
        var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(correoEnvio.trim())) {
            setMensajeFeedbackEnvio({ tipo: 'danger', texto: 'Por favor ingrese un correo electrónico válido.' });
            return;
        }

        if (!targetIdCarrito) {
            setMensajeFeedbackEnvio({ tipo: 'danger', texto: 'No se identificó el ID del carrito para enviar la cotización.' });
            return;
        }

        setEnviandoCorreo(true);
        setMensajeFeedbackEnvio(null);

        var payload = {
            empresa: emp || "AIGMETROPOLITANA",
            plataforma: plataforma1 || "WEB",
            idCarrito: targetIdCarrito,
            correo: correoEnvio.trim(),
            accion: "enviar"
        };

        try {
            var url = urlEnviarPdfCotizacionMoto + targetIdCarrito;
            var res = await httpClient.post(url, payload);
            if (res && res.transaccion !== false) {
                setMensajeFeedbackEnvio({ tipo: 'success', texto: 'Cotización enviada exitosamente a: ' + correoEnvio });
            } else {
                var errTxt = (res && res.errorDescripcion) || 'No se pudo enviar el correo de cotización.';
                setMensajeFeedbackEnvio({ tipo: 'danger', texto: errTxt });
            }
        } catch (err) {
            console.error('Error enviando cotización por correo:', err);
            setMensajeFeedbackEnvio({ tipo: 'danger', texto: 'Error de conexión al enviar el correo.' });
        } finally {
            setEnviandoCorreo(false);
        }
    };

    return (
        <Card className="shadow-sm mb-4" style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <CardBody style={{ padding: '2rem' }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '1.5rem', color: '#000000' }}>
                    Valor del Seguro
                </h4>

                {/* Prima Neta */}
                <Row className="mb-2">
                    <Col xs={6}>
                        <h5 style={{ margin: 0 }}>Prima Neta</h5>
                    </Col>
                    <Col xs={6} className="text-right">
                        <h5 style={{ margin: 0 }}>{formatearMoneda(primaNeta)}</h5>
                    </Col>
                </Row>

                {/* Impuestos (Acordeón clickeable) */}
                <div 
                    onClick={() => setMostrarImpuestos(!mostrarImpuestos)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Presione click para ver/ocultar el detalle de impuestos"
                >
                    <Row className="mb-2">
                        <Col xs={6}>
                            <h5 style={{ margin: 0 }}>
                                Impuestos <i className="fa fa-info-circle" aria-hidden="true" style={{ cursor: 'pointer' }}></i>
                            </h5>
                        </Col>
                        <Col xs={6} className="text-right">
                            <h5 style={{ margin: 0 }}>{formatearMoneda(totalImpuestos)}</h5>
                        </Col>
                    </Row>
                </div>

                {/* Detalle Desglosado de Impuestos (Colapsable) */}
                {mostrarImpuestos && (
                    <div className="pl-3 mb-2">
                        <Row className="mb-1">
                            <Col xs={6}>
                                <h5 className="text-muted" style={{ margin: 0 }}>IVA</h5>
                            </Col>
                            <Col xs={6} className="text-right">
                                <h5 className="text-muted" style={{ margin: 0 }}>{formatearMoneda(iva)}</h5>
                            </Col>
                        </Row>

                        <Row className="mb-1">
                            <Col xs={6}>
                                <h5 className="text-muted" style={{ margin: 0 }}>Super bancos 3.5 %</h5>
                            </Col>
                            <Col xs={6} className="text-right">
                                <h5 className="text-muted" style={{ margin: 0 }}>{formatearMoneda(superBancos)}</h5>
                            </Col>
                        </Row>

                        <Row className="mb-1">
                            <Col xs={6}>
                                <h5 className="text-muted" style={{ margin: 0 }}>Seguro Campesino</h5>
                            </Col>
                            <Col xs={6} className="text-right">
                                <h5 className="text-muted" style={{ margin: 0 }}>{formatearMoneda(seguroCampesino)}</h5>
                            </Col>
                        </Row>

                        <Row className="mb-1">
                            <Col xs={6}>
                                <h5 className="text-muted" style={{ margin: 0 }}>Derechos de Emisión</h5>
                            </Col>
                            <Col xs={6} className="text-right">
                                <h5 className="text-muted" style={{ margin: 0 }}>{formatearMoneda(derechosEmision)}</h5>
                            </Col>
                        </Row>
                    </div>
                )}

                <hr className="my-3" />

                {/* Prima Total */}
                <Row className="align-items-center mb-3">
                    <Col xs={6}>
                        <h5 style={{ fontWeight: 'bold', margin: 0 }}>
                            Prima Total
                        </h5>
                    </Col>
                    <Col xs={6} className="text-right">
                        <h5 style={{ fontWeight: 'bold', margin: 0 }}>
                            {formatearMoneda(primaTotal)}
                        </h5>
                    </Col>
                </Row>

                {/* Casilla Aceptar Términos y Condiciones (Solo si requiereTerminos y no está bloqueada la emisión) */}
                {requiereTerminos && !esBloqueo && (
                    <div className="text-center pt-2 pb-3">
                        <div 
                            className="d-inline-flex align-items-center" 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setAceptaTerminos(!aceptaTerminos)}
                        >
                            <div 
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    border: '2px solid #001871',
                                    borderRadius: '3px',
                                    backgroundColor: aceptaTerminos ? '#001871' : '#ffffff',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease-in-out',
                                    flexShrink: 0
                                }}
                            >
                                {aceptaTerminos && (
                                    <i className="fa fa-check" style={{ color: '#ffffff', fontSize: '11px', fontWeight: 'bold' }} />
                                )}
                            </div>
                            <span
                                style={{
                                    fontSize: '14px',
                                    color: '#001871',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                Aceptar Términos y Condiciones
                            </span>
                        </div>
                    </div>
                )}

                {/* Botón Dinámico de Acción: ACTUALIZAR o CONTINUAR / INICIAR EMISIÓN o DISCLAIMER DE BLOQUEO */}
                <div className="text-center pt-1">
                    {huboCambio ? (
                        <Button 
                            className={"btn btn-primary" + (deshabilitarAcciones ? "" : " btn-purple fe-pulse")}
                            style={{ 
                                display: "block", 
                                margin: "auto", 
                                width: "100%", 
                                padding: "10px 15px", 
                                fontWeight: "bold", 
                                textTransform: "uppercase",
                                backgroundColor: deshabilitarAcciones ? '#7987a1' : undefined,
                                borderColor: deshabilitarAcciones ? '#7987a1' : undefined,
                                opacity: deshabilitarAcciones ? 0.65 : 1,
                                cursor: deshabilitarAcciones ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => {
                                if (deshabilitarAcciones) return;
                                if (onActualizar) onActualizar();
                            }}
                            disabled={actualizando || deshabilitarAcciones}
                        >
                            <i className="fa fa-chevron-circle-right mr-2" aria-hidden="true"></i> 
                            {actualizando ? 'ACTUALIZANDO...' : 'ACTUALIZAR'}
                        </Button>
                    ) : esBloqueo ? (
                        <div 
                            className="alert alert-warning mb-0 text-center"
                            style={{
                                backgroundColor: '#fff8e6',
                                border: '1px solid #ffe29a',
                                borderRadius: '4px',
                                padding: '12px 16px',
                                color: '#8a6d3b',
                                fontSize: '13px',
                                fontWeight: '600',
                                lineHeight: '1.4'
                            }}
                        >
                            <i className="fa fa-info-circle mr-2" style={{ fontSize: '15px', color: '#e67e22' }} aria-hidden="true"></i>
                            {mensajeBloqueo}
                        </div>
                    ) : (
                        <Button 
                            className="btn btn-primary"
                            style={{ 
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "auto",
                                padding: "9px 24px",
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                backgroundColor: ((requiereTerminos && !aceptaTerminos) || deshabilitarAcciones) ? '#7987a1' : '#001871',
                                borderColor: ((requiereTerminos && !aceptaTerminos) || deshabilitarAcciones) ? '#7987a1' : '#001871',
                                opacity: ((requiereTerminos && !aceptaTerminos) || deshabilitarAcciones) ? 0.7 : 1,
                                cursor: ((requiereTerminos && !aceptaTerminos) || deshabilitarAcciones) ? 'not-allowed' : 'pointer',
                                borderRadius: '4px'
                            }}
                            onClick={() => {
                                if (deshabilitarAcciones) return;
                                if (requiereTerminos && !aceptaTerminos) return;
                                if (onIniciarEmision) onIniciarEmision();
                            }}
                            disabled={(requiereTerminos && !aceptaTerminos) || deshabilitarAcciones}
                        >
                            <i className="fa fa-chevron-circle-right mr-2" aria-hidden="true"></i> 
                            {textoBotonEmision || 'INICIAR EMISIÓN'}
                        </Button>
                    )}
                </div>

                {/* Sección de Descarga PDF y Enviar por Correo */}
                {!huboCambio && mostrarSeccionDescarga && mostrarDescargaPdf && (
                    <div className="pt-4 mt-2">
                        {mensajeFeedbackEnvio && (
                            <Alert color={mensajeFeedbackEnvio.tipo} className="py-2 mb-3" style={{ fontSize: '13px' }}>
                                <i className={"fa " + (mensajeFeedbackEnvio.tipo === 'success' ? "fa-check-circle" : "fa-exclamation-circle") + " mr-1"}></i>
                                {mensajeFeedbackEnvio.texto}
                            </Alert>
                        )}

                        {/* Botón Descargar Cotización */}
                        <div className="mb-3">
                            <Button
                                className="btn btn-detail-white"
                                style={{
                                    width: 'auto',
                                    padding: '8px 14px',
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    borderRadius: '4px'
                                }}
                                onClick={handleDescargarPdf}
                                disabled={cargandoPdf}
                            >
                                {cargandoPdf ? (
                                    <Spinner size="sm" className="mr-2" />
                                ) : (
                                    <i className="fa fa-cloud-download mr-2" aria-hidden="true"></i>
                                )}
                                DESCARGAR COTIZACIÓN
                            </Button>
                        </div>

                        {/* Input Correo y Botón Enviar */}
                        <Row className="no-gutters align-items-center">
                            <Col xs={8} className="pr-2">
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder="Por favor ingrese el correo"
                                    value={correoEnvio}
                                    onChange={(e) => setCorreoEnvio(e.target.value)}
                                    style={{ fontSize: '13px', height: '38px' }}
                                    disabled={enviandoCorreo}
                                />
                            </Col>
                            <Col xs={4}>
                                <Button
                                    className="btn btn-detail-white"
                                    style={{
                                        width: '100%',
                                        padding: '8px 10px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        borderRadius: '4px'
                                    }}
                                    onClick={handleEnviarCorreo}
                                    disabled={enviandoCorreo || !correoEnvio}
                                >
                                    {enviandoCorreo ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <>
                                            <i className="fa fa-envelope-o mr-1" aria-hidden="true"></i>
                                            ENVIAR
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default ValoresCotizacionMoto;
